import React, { useState, useEffect } from 'react';
import { WorkoutSelector } from './WorkoutSelector';
import { WorkoutOverview } from './WorkoutOverview';
import { WorkoutSession } from './WorkoutSession';
import { workoutService } from '../services/workout';
import { activeWorkoutService } from '../services/activeWorkout';
import { useAuth } from '../contexts/AuthContext';
import type { ProgramWorkout } from '../types';

interface WorkoutManagerProps {
  onBack: () => void;
}

type WorkoutState = 'selecting' | 'overview' | 'in-progress' | 'completed';

/**
 * WorkoutManager - Main component for workout management
 * Handles workout selection, execution, and completion flow
 * Requirements: 5.3, 6.1, 6.2
 */
export const WorkoutManager: React.FC<WorkoutManagerProps> = ({ onBack }) => {
  const { user } = useAuth();
  const [workoutState, setWorkoutState] = useState<WorkoutState>('selecting');
  const [selectedWorkout, setSelectedWorkout] = useState<ProgramWorkout | null>(null);
  const [startingExerciseIndex, setStartingExerciseIndex] = useState<number>(0);
  const [completedWorkoutData, setCompletedWorkoutData] = useState<any>(null);

  // Check for active workout on mount
  useEffect(() => {
    const activeWorkout = activeWorkoutService.getActiveWorkout();
    if (activeWorkout) {
      // Resume active workout - go directly to in-progress
      setSelectedWorkout(activeWorkout.workout);
      setStartingExerciseIndex(activeWorkout.currentExerciseIndex || 0);
      setWorkoutState('in-progress');
      console.log('Resuming active workout:', activeWorkout.workout.name);
    }
  }, []);

  const handleWorkoutSelect = (workout: ProgramWorkout) => {
    // Show overview screen first
    setSelectedWorkout(workout);
    setWorkoutState('overview');
  };

  const handleStartExercise = (exerciseIndex: number) => {
    if (!selectedWorkout) return;
    
    // Start new workout session
    activeWorkoutService.saveActiveWorkout({
      workout: selectedWorkout,
      startedAt: new Date().toISOString(),
      completedExercises: [],
      currentExerciseIndex: exerciseIndex,
      sets: {},
      notes: {}
    });
    
    setStartingExerciseIndex(exerciseIndex);
    setWorkoutState('in-progress');
  };

  const handleWorkoutComplete = async (workoutData: {
    workout_id: string;
    exercises: Array<{
      exercise_id: string;
      sets: any[];
      notes?: string;
    }>;
    notes?: string;
    duration_minutes: number;
  }) => {
    try {
      // Use the workout service to save the completed workout
      const completedWorkout = await workoutService.completeWorkout(
        workoutData, 
        user?.id, 
        selectedWorkout?.program_id
      );
      
      // Clear active workout from storage (don't throw if this fails)
      try {
        activeWorkoutService.clearActiveWorkout();
      } catch (clearError) {
        console.warn('Failed to clear active workout from storage:', clearError);
        // Don't throw - workout is already saved successfully
      }
      
      setCompletedWorkoutData({
        ...workoutData,
        completedWorkout
      });
      setWorkoutState('completed');
    } catch (error) {
      console.error('Failed to save workout:', error);
      throw error;
    }
  };

  const handleWorkoutCancel = () => {
    const confirmCancel = confirm('Вы уверены, что хотите отменить тренировку? Все данные будут потеряны.');
    if (confirmCancel) {
      // Clear active workout from storage
      activeWorkoutService.clearActiveWorkout();
      
      setSelectedWorkout(null);
      setWorkoutState('selecting');
    }
  };

  const handleBackToSelection = () => {
    setSelectedWorkout(null);
    setCompletedWorkoutData(null);
    setStartingExerciseIndex(0);
    setWorkoutState('selecting');
  };

  const handleBackToMain = () => {
    setSelectedWorkout(null);
    setCompletedWorkoutData(null);
    setStartingExerciseIndex(0);
    setWorkoutState('selecting');
    onBack();
  };

  const handleOverviewCancel = () => {
    setSelectedWorkout(null);
    setWorkoutState('selecting');
  };

  // Workout Selection State
  if (workoutState === 'selecting') {
    return (
      <WorkoutSelector
        onWorkoutSelect={handleWorkoutSelect}
        onCancel={onBack}
      />
    );
  }

  // Workout Overview State
  if (workoutState === 'overview' && selectedWorkout) {
    return (
      <WorkoutOverview
        workout={selectedWorkout}
        onStartExercise={handleStartExercise}
        onCancel={handleOverviewCancel}
      />
    );
  }

  // Workout In Progress State
  if (workoutState === 'in-progress' && selectedWorkout) {
    return (
      <WorkoutSession
        workout={selectedWorkout}
        onComplete={handleWorkoutComplete}
        onCancel={handleWorkoutCancel}
        startingExerciseIndex={startingExerciseIndex}
      />
    );
  }

  // Workout Completed State
  if (workoutState === 'completed' && completedWorkoutData) {
    return (
      <div className="workout-completed p-4 max-w-2xl mx-auto">
        <div className="text-center mb-8">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-2xl font-bold text-green-600 mb-2">
            Тренировка завершена!
          </h1>
          <p className="text-gray-600">
            Отличная работа! Тренировка сохранена.
          </p>
        </div>

        {/* Workout Summary */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
          <h2 className="text-lg font-semibold mb-4">Сводка тренировки</h2>
          
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="text-center p-3 bg-blue-50 rounded-lg">
              <div className="text-2xl font-bold text-blue-600">
                {completedWorkoutData.duration_minutes}
              </div>
              <div className="text-sm text-blue-600">минут</div>
            </div>
            <div className="text-center p-3 bg-green-50 rounded-lg">
              <div className="text-2xl font-bold text-green-600">
                {completedWorkoutData.exercises.length}
              </div>
              <div className="text-sm text-green-600">упражнений</div>
            </div>
          </div>

          {/* Exercise Details */}
          <div className="space-y-3">
            {completedWorkoutData.exercises.map((exercise: any, index: number) => {
              const totalSets = exercise.sets.length;
              const totalReps = exercise.sets.reduce((sum: number, set: any) => sum + (set.reps || 0), 0);
              const maxWeight = Math.max(...exercise.sets.map((set: any) => set.weight || 0), 0);
              
              // Find the exercise name from selectedWorkout
              const exerciseInfo = selectedWorkout?.exercises.find(
                (ex) => ex.exercise.id === exercise.exercise_id
              );
              const exerciseName = exerciseInfo?.exercise.name_ru || `Упражнение ${index + 1}`;
              
              return (
                <div key={exercise.exercise_id} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0">
                  <div>
                    <div className="font-medium">{exerciseName}</div>
                    {exercise.notes && (
                      <div className="text-sm text-gray-600">{exercise.notes}</div>
                    )}
                  </div>
                  <div className="text-right text-sm text-gray-600">
                    <div>{totalSets} подходов</div>
                    <div>{totalReps} повторов</div>
                    {maxWeight > 0 && <div>макс. {maxWeight} кг</div>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Workout Notes */}
          {completedWorkoutData.notes && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm font-medium text-gray-700 mb-1">Заметки:</div>
              <div className="text-sm text-gray-600">{completedWorkoutData.notes}</div>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleBackToSelection}
            className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700"
          >
            Начать новую тренировку
          </button>
          <button
            onClick={handleBackToMain}
            className="w-full border border-gray-300 text-gray-700 py-3 px-4 rounded-lg hover:bg-gray-50"
          >
            Вернуться в главное меню
          </button>
        </div>

        {/* Motivational Message */}
        <div className="mt-6 p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-200 rounded-lg">
          <div className="text-center">
            <div className="text-lg font-medium text-gray-800 mb-1">
              Продолжайте в том же духе! 💪
            </div>
            <div className="text-sm text-gray-600">
              Регулярные тренировки - ключ к успеху
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fallback state
  return (
    <div className="workout-manager p-4 max-w-2xl mx-auto">
      <div className="text-center py-8">
        <div className="text-4xl mb-4">⚠️</div>
        <p className="text-gray-600 mb-4">Произошла ошибка</p>
        <button
          onClick={handleBackToMain}
          className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
        >
          Вернуться в главное меню
        </button>
      </div>
    </div>
  );
};

export default WorkoutManager;